import React from "react";
import { Modal } from "react-bootstrap";
import { FiDownload, FiFileText, FiX } from "react-icons/fi";
import StatusChip from "../../../../Common/StatusChip";
import { formatAmountWithCommas } from "../../../../../Utils/utils";
import { quoteChipType } from "./quotesService";

const QuoteDetailsModal = ({ open, onClose, quote }) => {
  if (!open || !quote) {
    return null;
  }

  return (
    <Modal show={open} onHide={onClose} size="lg" centered>
      <Modal.Header
        style={{
          borderBottom: "1px solid #e9ecef",
          backgroundColor: "#f8fbfb",
          padding: "16px 24px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: "12px" }}>
            <h5 className="m-0" style={{ color: "#056363", fontWeight: 700 }}>
              Quotation Details: {quote.QuoteNo}
            </h5>
            <StatusChip
              status={quote.Status || "Received"}
              type={quoteChipType(quote.Status || "Received")}
            />
          </div>
          <button
            type="button"
            className="btn btn-link text-muted p-0"
            onClick={onClose}
            style={{ fontSize: "1.2rem", textDecoration: "none" }}
          >
            <FiX />
          </button>
        </div>
      </Modal.Header>

      <Modal.Body style={{ padding: "24px" }}>
        {/* Info Grid */}
        <div
          style={{
            backgroundColor: "#f9fafb",
            borderRadius: "8px",
            border: "1px solid #e5e7eb",
            padding: "16px 20px",
            marginBottom: "20px",
          }}
        >
          <div className="row g-3">
            <div className="col-md-4 mb-2">
              <span className="text-muted d-block" style={{ fontSize: "12px" }}>
                Vendor / Supplier
              </span>
              <strong style={{ color: "#111827", fontSize: "14px" }}>
                {quote.VendorName || "N/A"}
              </strong>
            </div>

            <div className="col-md-4 mb-2">
              <span className="text-muted d-block" style={{ fontSize: "12px" }}>
                Project / Site
              </span>
              <strong style={{ color: "#111827", fontSize: "14px" }}>
                {quote.ProjectName || "N/A"}
              </strong>
            </div>

            <div className="col-md-4 mb-2">
              <span className="text-muted d-block" style={{ fontSize: "12px" }}>
                Linked Requisition
              </span>
              <span
                className="badge badge-info"
                style={{
                  backgroundColor: "#e0f2fe",
                  color: "#0369a1",
                  fontSize: "12px",
                  padding: "5px 8px",
                  fontWeight: 600,
                }}
              >
                {quote.RequisitionNo || "N/A"}
              </span>
            </div>

            <div className="col-md-4 mb-2">
              <span className="text-muted d-block" style={{ fontSize: "12px" }}>
                Quote Date
              </span>
              <span style={{ fontSize: "13px", color: "#374151" }}>{quote.QuoteDate || "N/A"}</span>
            </div>

            <div className="col-md-4 mb-2">
              <span className="text-muted d-block" style={{ fontSize: "12px" }}>
                Validity / Expiry
              </span>
              <span style={{ fontSize: "13px", color: "#374151" }}>{quote.ValidTill || "N/A"}</span>
            </div>

            <div className="col-md-4 mb-2">
              <span className="text-muted d-block" style={{ fontSize: "12px" }}>
                Payment & Credit Terms
              </span>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#056363" }}>
                {quote.PaymentTerms || "N/A"}
              </span>
            </div>

            <div className="col-md-4 mb-2">
              <span className="text-muted d-block" style={{ fontSize: "12px" }}>
                Delivery Timeline
              </span>
              <span style={{ fontSize: "13px", color: "#374151" }}>
                {quote.DeliveryLeadTime || "Standard"}
              </span>
            </div>

            <div className="col-md-8 mb-2">
              <span className="text-muted d-block" style={{ fontSize: "12px" }}>
                Attached Quote Document
              </span>
              {quote.AttachmentName ? (
                <div
                  className="d-inline-flex align-items-center mt-1"
                  style={{
                    backgroundColor: "#fff",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    padding: "4px 10px",
                    gap: "8px",
                    fontSize: "12px",
                    color: "#056363",
                    fontWeight: 500,
                  }}
                >
                  <FiFileText color="#dc2626" size="14" />
                  {quote.AttachmentUrl ? (
                    <a
                      href={quote.AttachmentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "inherit" }}
                    >
                      {quote.AttachmentName}
                    </a>
                  ) : (
                    <span>{quote.AttachmentName}</span>
                  )}
                  <FiDownload
                    size="13"
                    style={{ cursor: "pointer", marginLeft: "4px" }}
                    title="Download Quote PDF"
                    onClick={() =>
                      quote.AttachmentUrl && window.open(quote.AttachmentUrl, "_blank")
                    }
                  />
                </div>
              ) : (
                <span className="text-muted" style={{ fontSize: "13px" }}>
                  No attachment
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Quoted Items Table */}
        <h6 style={{ color: "#056363", fontWeight: 600, marginBottom: "12px" }}>
          Quoted Materials & Rates
        </h6>

        <div
          className="table-responsive"
          style={{ border: "1px solid #e5e7eb", borderRadius: "6px" }}
        >
          <table className="table table-sm table-hover mb-0" style={{ fontSize: "13px" }}>
            <thead style={{ backgroundColor: "#E8FBFB" }}>
              <tr>
                <th style={{ width: "40px", padding: "10px" }}>#</th>
                <th style={{ padding: "10px" }}>Material / Description</th>
                <th style={{ padding: "10px" }}>Specification</th>
                <th style={{ width: "80px", padding: "10px", textAlign: "right" }}>Qty</th>
                <th style={{ width: "80px", padding: "10px" }}>UOM</th>
                <th style={{ width: "130px", padding: "10px", textAlign: "right" }}>
                  Unit Rate (PKR)
                </th>
                <th style={{ width: "140px", padding: "10px", textAlign: "right" }}>Total (PKR)</th>
              </tr>
            </thead>
            <tbody>
              {quote.Items && quote.Items.length > 0 ? (
                quote.Items.map((item, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: "10px", verticalAlign: "middle" }}>{idx + 1}</td>
                    <td style={{ padding: "10px", verticalAlign: "middle", fontWeight: 600 }}>
                      {item.MaterialName || item.ItemName || "N/A"}
                    </td>
                    <td style={{ padding: "10px", verticalAlign: "middle", color: "#6b7280" }}>
                      {item.Specification || "Standard"}
                    </td>
                    <td style={{ padding: "10px", verticalAlign: "middle", textAlign: "right" }}>
                      {item.Qty}
                    </td>
                    <td style={{ padding: "10px", verticalAlign: "middle" }}>{item.Uom}</td>
                    <td
                      style={{
                        padding: "10px",
                        verticalAlign: "middle",
                        textAlign: "right",
                        fontWeight: 600,
                      }}
                    >
                      {formatAmountWithCommas(item.UnitRate)}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        verticalAlign: "middle",
                        textAlign: "right",
                        fontWeight: 700,
                        color: "#056363",
                      }}
                    >
                      {formatAmountWithCommas(item.TotalAmount || item.Qty * item.UnitRate)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-3 text-muted">
                    No items recorded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Totals & Remarks */}
        <div className="row mt-3">
          <div className="col-md-7">
            {quote.Remarks && (
              <div
                style={{
                  backgroundColor: "#fffbeb",
                  border: "1px solid #fef3c7",
                  padding: "10px 14px",
                  borderRadius: "6px",
                  fontSize: "12px",
                }}
              >
                <strong style={{ color: "#92400e" }}>Vendor Terms / Remarks:</strong>
                <p className="m-0 mt-1" style={{ color: "#78350f" }}>
                  {quote.Remarks}
                </p>
              </div>
            )}
          </div>

          <div className="col-md-5">
            <div
              style={{
                backgroundColor: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                padding: "12px 16px",
                fontSize: "13px",
              }}
            >
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">Items Subtotal:</span>
                <span>PKR {formatAmountWithCommas(quote.ItemsTotal || 0)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Carriage / Freight:</span>
                <span>PKR {formatAmountWithCommas(quote.FreightAmount || 0)}</span>
              </div>
              <div
                className="d-flex justify-content-between pt-2"
                style={{
                  borderTop: "1px solid #d1d5db",
                  fontWeight: 700,
                  fontSize: "14px",
                  color: "#056363",
                }}
              >
                <span>Grand Total:</span>
                <span>PKR {formatAmountWithCommas(quote.GrandTotal || 0)}</span>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer style={{ borderTop: "1px solid #e9ecef", padding: "12px 24px" }}>
        <button type="button" className="btn btn-secondary px-4" onClick={onClose}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default QuoteDetailsModal;
